import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ArrowDownCircleFill from '@/assets/icons/arrow_down_circle_fill.svg?component';
import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import FirstAidKitFill from '@/assets/icons/first_aid_kit_fill.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { Card, CardDivider, CardHeader, CardImage } from '@/components/atoms/Card';
import { Spinner } from '@/components/atoms/Spinner';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import { renderTrendIcon, TrendIconEnum } from '@/components/atoms/TrendIcon/TrendIconUtils';
import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import { EmptyState } from '@/components/molecules/EmptyState';
import MetricCard from '@/components/molecules/MetricCard/MetricCard';
import { MetricSummaryRow } from '@/components/molecules/MetricSummaryRow';
import {
    describeTrend,
    type TrendDirection,
    trendHelpText,
} from '@/components/molecules/OverviewValueWithTrendTooltip/describeTrend';
import { WoundDescription } from '@/components/molecules/WoundDescription';
import type { Image } from '@/components/organisms/ImageViewer';
import settings from '@/config/settings';
import type { WelfareOverview } from '@/domain/client';
import type { BodyWoundsData, WelfareData } from '@/domain/welfare/types';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { computeDateRange } from '@/utils/date';
import { formatFractionAsPercentageByLocale } from '@/utils/localeFormatters';
import { createCageUrl } from '@/utils/urlGenerator';

import styles from './CageWelfareWoundsDashboard.module.scss';

const PERCENTAGE_DECIMAL_PLACES = 2;
export interface CageWelfareWoundsDashboardPageContentProps {
    clientId: number;
    locationId: number;
    cageId: number;
    fishHealthData: WelfareData;
    bodyWoundsData: BodyWoundsData;
    images?: Image[];
    imagesLoading: boolean;
    cageFilterHandler?: (data: DateRange) => void;
    cageWelfareOverview: WelfareOverview | undefined;
    /** Calendar days (YYYY-MM-DD). The picker reads an instant as a day in the browser's zone. */
    inputDateObject?: DateRange;
    /** Licence held and a species the estimate suits; the container decides both. */
    showDowngradeEstimate?: boolean;
}

// This page shows the flat arrow while stable or without data, as it always has.
const WOUND_TREND_ICON: Record<TrendDirection, TrendIconEnum> = {
    noData: TrendIconEnum.TrendStable,
    stable: TrendIconEnum.TrendStable,
    growing: TrendIconEnum.TrendNegativeUp,
    decreasing: TrendIconEnum.TrendPositiveDown,
};

export default function CageWelfareWoundsDashboardPageContent(
    props: CageWelfareWoundsDashboardPageContentProps
) {
    const {
        clientId,
        locationId,
        cageId,
        fishHealthData,
        bodyWoundsData,
        images,
        imagesLoading,
        cageFilterHandler,
        cageWelfareOverview,
        inputDateObject,
        showDowngradeEstimate = false,
    } = props;
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { enabled: hasDateRange } = usePostHogFeatureFlag('wound-insight-date-range');
    const [woundsInfoTooltip, setWoundsInfoTooltip] = useState('');
    const [woundsInfoTrend, setWoundsInfoTrend] = useState(TrendIconEnum.TrendStable);
    const { lastEstimate: lastEstimateWelfare, lastDay: lastWelfareDay } = fishHealthData;
    const { lastEstimate: lastEstimateBodyWounds } = bodyWoundsData;

    const setupWelfareDataFromClientOverview = useCallback(() => {
        if (cageWelfareOverview) {
            const welfareWoundGrowth = Number.isNaN(cageWelfareOverview.woundSlope)
                ? '---'
                : formatFractionAsPercentageByLocale(
                      cageWelfareOverview.woundSlope,
                      i18n.language,
                      0
                  );

            // The shared reading: a NaN slope is stable rather than no text, and a stable trend
            // clears an arrow left from an earlier reading.
            const direction = describeTrend(
                cageWelfareOverview.woundRate,
                cageWelfareOverview.woundSlope,
                settings.woundGrowthLimit
            );
            setWoundsInfoTrend(WOUND_TREND_ICON[direction]);
            setWoundsInfoTooltip(trendHelpText(direction, welfareWoundGrowth, t));
        } else {
            setWoundsInfoTooltip(t('common.labels.noDataFound'));
        }
    }, [cageWelfareOverview, t]);

    useEffect(() => {
        setupWelfareDataFromClientOverview();
    }, [setupWelfareDataFromClientOverview]);

    // TODO: Replace with proper date range handling when welfare page date picker is refactored
    const { dateFrom, dateTo } = computeDateRange(lastWelfareDay, inputDateObject);

    return (
        <>
            {/* WB-1252: the date picker, for the clients the flag is released to. */}
            {hasDateRange ? (
                <div className={styles.datePickerContainer}>
                    <DateRangePicker
                        onDateChange={cageFilterHandler}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                    />
                </div>
            ) : null}

            <div className={styles.pageBoxLayout}>
                <div className={styles.leftSideLayout}>
                    <div className={styles.infoBoxContainer}>
                        <Tooltip content={woundsInfoTooltip}>
                            <MetricCard
                                title={t('cageWelfare.common.totalNumberOfWounds')}
                                icon={FirstAidKitFill}
                                tone="success"
                                value={formatFractionAsPercentageByLocale(
                                    lastEstimateWelfare.wound,
                                    i18n.language
                                )}
                                framed="surface"
                                trend={renderTrendIcon(woundsInfoTrend)}
                            />
                        </Tooltip>
                        {showDowngradeEstimate && (
                            <Tooltip
                                content={t('harvestReport.common.estDowngradeTooltip')}
                                maxWidth={450}>
                                <MetricCard
                                    title={t('common.labels.estimatedDowngrade')}
                                    icon={ArrowDownCircleFill}
                                    tone="danger"
                                    value={
                                        lastEstimateWelfare?.notSuperior
                                            ? formatFractionAsPercentageByLocale(
                                                  lastEstimateWelfare?.notSuperior,
                                                  i18n.language
                                              )
                                            : '-'
                                    }
                                    framed="surface"
                                />
                            </Tooltip>
                        )}
                    </div>
                    <Card
                        padding="lg"
                        header={
                            <CardHeader
                                title={t('cageWelfare.wounds.woundDescription')}
                                level="h2"
                                size="card"
                            />
                        }>
                        <WoundDescription
                            woundsColor={{
                                noWounds: lastEstimateBodyWounds.noWound,
                                bigWound: lastEstimateBodyWounds.bigWound,
                                mediumWound: lastEstimateBodyWounds.mediumWound,
                                smallWound: lastEstimateBodyWounds.smallWound,
                            }}
                        />
                    </Card>
                </div>

                <div className={styles.rightSideLayout}>
                    <Card
                        padding="lg"
                        header={
                            <CardHeader
                                title={t('common.labels.detections')}
                                level="h2"
                                size="card"
                                actions={
                                    <Button
                                        variant={'secondary'}
                                        trackingId="welfare_wounds_view_detections"
                                        onClick={() =>
                                            navigate(
                                                `${createCageUrl(
                                                    clientId,
                                                    locationId,
                                                    cageId
                                                )}/welfare/wounds/detections`
                                            )
                                        }
                                        iconEnd={
                                            <SvgIcon
                                                color="currentColor"
                                                icon={ArrowRightLine}
                                                size="custom"
                                                customSize={20}
                                            />
                                        }>
                                        {t('common.actions.view')}
                                    </Button>
                                }
                            />
                        }>
                        <div>
                            {imagesLoading ? (
                                <Spinner size="lg" />
                            ) : images && images.length > 0 ? (
                                <CardImage
                                    src={images[images.length - 1]?.path}
                                    alt={t('atoms.detectionImage.alt')}
                                />
                            ) : (
                                <EmptyState title={t('common.labels.atTheMomentWeDo')} />
                            )}
                        </div>
                    </Card>
                    <Card
                        padding="lg"
                        header={
                            <CardHeader
                                title={t('cageWelfare.wounds.woundDistribution')}
                                level="h2"
                                size="card"
                            />
                        }>
                        <MetricSummaryRow
                            flush
                            label={t('fishHealthIndicators.noWound')}
                            value={formatFractionAsPercentageByLocale(
                                lastEstimateBodyWounds.noWound,
                                i18n.language,
                                PERCENTAGE_DECIMAL_PLACES
                            )}
                        />
                        <CardDivider />
                        <MetricSummaryRow
                            flush
                            label={t('fishHealthIndicators.oneWound')}
                            value={formatFractionAsPercentageByLocale(
                                lastEstimateBodyWounds.oneWound,
                                i18n.language,
                                PERCENTAGE_DECIMAL_PLACES
                            )}
                        />
                        <CardDivider />
                        <MetricSummaryRow
                            flush
                            label={t('fishHealthIndicators.moreThanOneWound')}
                            value={formatFractionAsPercentageByLocale(
                                lastEstimateBodyWounds.moreThanOneWound,
                                i18n.language,
                                PERCENTAGE_DECIMAL_PLACES
                            )}
                        />
                        <CardDivider />
                    </Card>
                </div>
            </div>
        </>
    );
}
